// Utils
import { component } from 'picoapp';
import Swiper, { A11y, Autoplay, Manipulation, Mousewheel, Navigation, Scrollbar } from 'swiper';

import { setCSSProperty } from 'utils/css';

import choozy from 'utils/choozy';

Swiper.use([A11y, Autoplay, Navigation, Manipulation, Mousewheel, Scrollbar]);

// eslint-disable-next-line
const imagesObserver = (function () {
  let observer = null;
  let callback;
  let options;
  let nodes = [];

  // Private
  const handleIntersection = (entries) => {
    if (typeof callback === 'function') callback(entries);
  };

  // Public
  const start = () => {
    if (observer) {
      observer.disconnect();
    }

    if (typeof options !== 'object') return;

    observer = new IntersectionObserver(handleIntersection, options);

    if (nodes.length > 0) {
      nodes.forEach((_node) => observer.observe(_node));
    }
  };

  const stop = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  const setNodes = (_nodes) => {
    nodes = _nodes;

    start();
  };

  const setCallback = (_callback) => {
    callback = _callback;

    start();
  };

  const setOptions = (_options) => {
    options = _options;

    start();
  };

  const observe = (_node) => {
    nodes.push(_node);

    if (observer) observer.observe(_node);
  };

  return {
    start,
    stop,
    setNodes,
    setCallback,
    setOptions,
    observe,
  };
})();

export default component(async (node) => {
  const { slider, image, thumbnail, progressIndicator } = choozy(node);

  const images = [].concat(image);
  const thumbnails = [].concat(thumbnail);
  const progressIndicators = [].concat(progressIndicator);

  if (window.innerWidth < 1024) {
    const carousel = new Swiper(slider, {
      slidesPerView: 1,
      breakpoints: {
        1024: {
          init: false,
          enabled: false,
        },
      },
    });

    carousel.on('slideChange', ({ activeIndex }) => {
      progressIndicators.forEach((el) => {
        const index = parseInt(el.dataset.progressIndicator, 10);

        el.classList[index === activeIndex ? 'add' : 'remove']('active');
      });
    });
  }

  let visibleImages = [];
  let visibleImageIndex = 0;

  thumbnails.forEach((button) => {
    button.addEventListener('click', () => {
      const index = button.dataset.thumbnail;
      const relatedImage = images.find((img) => img.dataset.image === index);

      relatedImage.previousElementSibling.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  });

  imagesObserver.setNodes(images);
  imagesObserver.setCallback((entries) => {
    if (window.innerWidth < 1024) return;
    entries.forEach((entry) => {
      const index = parseInt(entry.target.dataset.image, 10);

      if (entry.isIntersecting) {
        visibleImages.push(index);
        visibleImageIndex = index;
      } else if (visibleImages.includes(index)) {
        visibleImages = visibleImages.filter((i) => i !== index);
        visibleImageIndex = visibleImages[visibleImages.length - 1];
      }
    });

    progressIndicators.forEach((el) => {
      const index = parseInt(el.dataset.progressIndicator, 10);

      el.classList[index === visibleImageIndex ? 'add' : 'remove']('active');
    });

    thumbnails.forEach((el) => {
      const index = parseInt(el.dataset.thumbnail, 10);

      el.classList[index === visibleImageIndex ? 'add' : 'remove']('active');
    });
  });

  const firstImage = images[0].firstElementChild;

  const resizeBottomOverlay = () => {
    const { top, height } = images[0].getBoundingClientRect();

    const overlayHeight = window.innerHeight - (top + height);

    setCSSProperty(node, '--overlay-height', `${overlayHeight}px`);
    imagesObserver.setOptions({
      root: null,
      threshold: 0.5,
      rootMargin: `0px 0px -${overlayHeight}px 0px`,
    });

    images.forEach((img) => img.classList.remove('opacity-0'));
  };

  document.addEventListener('theme:screen:size-updated', resizeBottomOverlay);
  if (firstImage.complete) {
    setTimeout(() => resizeBottomOverlay(), 200);
  } else {
    firstImage.addEventListener('load', () => {
      setTimeout(() => resizeBottomOverlay(), 200);
    });
  }
});
